import { useQuery } from "@tanstack/react-query";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { getDashboardSummary, getPlatformMetrics } from "@/lib/api/admin";
import { Spinner } from "@/components/Spinner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router";
import {
  Store,
  Trash2,
  UserMinus,
  Star,
  Wallet,
  Gift,
  PiggyBank,
  Calendar,
  CalendarCheck,
  UserCheck,
  LogIn,
  Percent,
} from "lucide-react";
import { formatInt, formatCents } from "@/lib/utils/number";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Matches Flutter's AppColors: warning=#F59E0B, success=#22C55E, error=#EF4444
function StatCard({
  icon: Icon,
  title,
  value,
  onClick,
  color,
}: {
  icon: React.ElementType;
  title: string;
  value: number;
  onClick?: () => void;
  color: string;
}) {
  return (
    <Card
      className={onClick ? "cursor-pointer transition-shadow hover:shadow-md" : undefined}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 pt-6">
        <div
          className="flex size-12 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}1a` }}
        >
          <Icon className="size-6" style={{ color }} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{formatInt(value)}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// Matches Flutter's ActivityChip: colored bg (10%) + colored border (30%) + count badge
function ActivityChip({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div
      className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-medium"
      style={{
        backgroundColor: `${color}1a`,
        border: `1px solid ${color}4d`,
        color,
      }}
    >
      {label}
      <span
        className="rounded-full px-1.5 text-[10px] font-bold leading-4 text-white"
        style={{ backgroundColor: color }}
      >
        {formatInt(count)}
      </span>
    </div>
  );
}

// Matches Flutter's SectionTitle: 18px, w600, textPrimary
function SectionTitle({ title }: { title: string }) {
  return <h2 className="text-lg font-semibold">{title}</h2>;
}

// Matches Flutter's MetricCard: icon in colored rounded bg + bold value + muted label
function MetricCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <Card className="w-full sm:w-44">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div
          className="flex size-9 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}1a` }}
        >
          <Icon className="size-5" style={{ color }} />
        </div>
        <p className="text-2xl font-bold leading-none">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const user = useAdminAuth();
  const navigate = useNavigate();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: async () => getDashboardSummary(user!),
    enabled: !!user,
  });

  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["platform-metrics"],
    queryFn: async () => getPlatformMetrics(user!),
    enabled: !!user,
    select: (d) => ({
      ...d,
      top_stores_by_discount: d.top_stores_by_discount ?? [],
      top_stores_by_reward: d.top_stores_by_reward ?? [],
      top_active_users: d.top_active_users ?? [],
      abuse_indicators: d.abuse_indicators ?? [],
      activity_breakdown: {
        ...d.activity_breakdown,
        daily_last_7_days: d.activity_breakdown?.daily_last_7_days ?? [],
      },
    }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Platform overview and analytics</p>
      </div>

      {/* Summary stat cards — matches Flutter DashboardPage */}
      {summaryLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-6 border-primary" />
        </div>
      ) : summary ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Store}
            title="Pending Approval"
            value={summary.pending_approval_count}
            color="#F59E0B"
            onClick={() => void navigate("/stores?status=inactive&sub=pending-approval")}
          />
          <StatCard
            icon={Store}
            title="Active Stores"
            value={summary.active_stores_count}
            color="#22C55E"
            onClick={() => void navigate("/stores?status=active")}
          />
          <StatCard
            icon={Trash2}
            title="Removal Requests"
            value={summary.removal_requests_count}
            color="#EF4444"
            onClick={() => void navigate("/stores/removal-requests")}
          />
          <StatCard
            icon={UserMinus}
            title="Deletion Requests"
            value={summary.deletion_requests_count}
            color="#EF4444"
            onClick={() => void navigate("/users/deletion-requests")}
          />
        </div>
      ) : null}

      {/* Analytics — matches Flutter MetricsDashboard */}
      {metricsLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-6 border-primary" />
        </div>
      ) : metrics ? (
        <div className="space-y-6">
          {/* Overview — matches Flutter OverviewSection */}
          <SectionTitle title="Overview" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Stores</p>
                <p className="text-2xl font-bold">{formatInt(metrics.store_counts.total)}</p>
                <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                  <span>
                    <span className="font-medium" style={{ color: "#22C55E" }}>
                      {formatInt(metrics.store_counts.active)}
                    </span>{" "}
                    active
                  </span>
                  <span>
                    <span className="font-medium" style={{ color: "#6366F1" }}>
                      {formatInt(metrics.store_counts.approved)}
                    </span>{" "}
                    approved
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{formatInt(metrics.user_counts.total)}</p>
                <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                  <span>
                    <span className="font-medium" style={{ color: "#22C55E" }}>
                      +{formatInt(metrics.user_counts.new_last_7d)}
                    </span>{" "}
                    7d
                  </span>
                  <span>
                    <span className="font-medium" style={{ color: "#3B82F6" }}>
                      +{formatInt(metrics.user_counts.new_last_30d)}
                    </span>{" "}
                    30d
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total Activity</p>
                <p className="text-2xl font-bold">{formatInt(metrics.activity_counts.total)}</p>
                <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                  <span>
                    <span className="font-medium" style={{ color: "#3B82F6" }}>
                      {formatInt(metrics.activity_counts.last_7_days)}
                    </span>{" "}
                    7d
                  </span>
                  <span>
                    <span className="font-medium" style={{ color: "#6366F1" }}>
                      {formatInt(metrics.activity_counts.last_30_days)}
                    </span>{" "}
                    30d
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Lifetime Savings</p>
                <p className="text-2xl font-bold">
                  {formatCents(metrics.points_economy.total_saved_lifetime_cents)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Points Economy — matches Flutter MetricCard wrap */}
          <SectionTitle title="Points Economy" />
          <div className="flex flex-wrap gap-4">
            <MetricCard
              icon={Star}
              label="Accumulated points"
              value={formatInt(metrics.points_economy.total_accumulated_points)}
              color="#F59E0B"
            />
            <MetricCard
              icon={Wallet}
              label="Outstanding points"
              value={formatInt(metrics.points_economy.total_outstanding_points)}
              color="#3B82F6"
            />
            <MetricCard
              icon={Gift}
              label="Rewards claimed"
              value={formatInt(metrics.points_economy.total_point_rewards_redeemed)}
              color="#22C55E"
            />
            <MetricCard
              icon={PiggyBank}
              label="Total saved"
              value={formatCents(metrics.points_economy.total_saved_lifetime_cents)}
              color="#6366F1"
            />
          </div>

          {/* Activity Breakdown — matches Flutter ActivityBreakdownSection */}
          <SectionTitle title="Activity Breakdown" />
          <Card>
            <CardContent className="flex flex-col gap-4 sm:flex-row pt-6">
              {/* Action chips — matches Flutter's ActionChip with colors */}
              <div className="flex flex-wrap content-start gap-x-2 gap-y-3 flex-1">
                <ActivityChip
                  label="Items claimed"
                  count={metrics.activity_breakdown.item_claimed}
                  color="#3B82F6"
                />
                <ActivityChip
                  label="Point rewards"
                  count={metrics.activity_breakdown.point_reward_redeemed}
                  color="#22C55E"
                />
                <ActivityChip
                  label="Stamps"
                  count={metrics.activity_breakdown.stamp_earned}
                  color="#F59E0B"
                />
                <ActivityChip
                  label="Stamp rewards"
                  count={metrics.activity_breakdown.stamp_reward_redeemed}
                  color="#6366F1"
                />
                <ActivityChip
                  label="Event check-ins"
                  count={metrics.activity_breakdown.event_checkin}
                  color="#8B5CF6"
                />
              </div>

              {/* Mini line chart (last 7 days) — matches Flutter's MiniBarChart */}
              {metrics.activity_breakdown.daily_last_7_days.length > 0 && (
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-muted-foreground mb-3">Last 7 Days</p>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={metrics.activity_breakdown.daily_last_7_days}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: string) => {
                          try {
                            const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
                            return days[new Date(v).getDay() === 0 ? 6 : new Date(v).getDay() - 1];
                          } catch {
                            return v.slice(5);
                          }
                        }}
                      />
                      <YAxis tick={{ fontSize: 10 }} width={28} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#6366F1"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Stores — matches Flutter TopStoresCard */}
          <SectionTitle title="Top Stores" />
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Discounts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {metrics.top_stores_by_discount.slice(0, 5).map((s, i) => (
                    <div key={s.store_id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-4 text-muted-foreground">{i + 1}.</span>
                        <span
                          className="cursor-pointer hover:underline"
                          onClick={() => void navigate(`/stores/${s.store_id}`)}
                        >
                          {s.store_name}
                        </span>
                      </div>
                      <span className="font-medium">{formatInt(s.count)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">By Rewards</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {metrics.top_stores_by_reward.slice(0, 5).map((s, i) => (
                    <div key={s.store_id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-4 text-muted-foreground">{i + 1}.</span>
                        <span
                          className="cursor-pointer hover:underline"
                          onClick={() => void navigate(`/stores/${s.store_id}`)}
                        >
                          {s.store_name}
                        </span>
                      </div>
                      <span className="font-medium">{formatInt(s.count)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Active Users — matches Flutter TopUsersCard */}
          {metrics.top_active_users.length > 0 && (
            <>
              <SectionTitle title="Top Active Users" />
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    {metrics.top_active_users.slice(0, 5).map((u, i) => (
                      <div key={u.user_uid} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-4 text-muted-foreground">{i + 1}.</span>
                          <span
                            className="cursor-pointer hover:underline"
                            onClick={() => void navigate(`/users/${u.user_uid}`)}
                          >
                            {u.first_name} {u.last_name}
                          </span>
                        </div>
                        <span className="font-medium text-muted-foreground">
                          {formatInt(u.activity_count)} activities
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Events — matches Flutter MetricCard wrap */}
          <SectionTitle title="Events" />
          <div className="flex flex-wrap gap-4">
            <MetricCard
              icon={Calendar}
              label="Upcoming"
              value={formatInt(metrics.events_summary.upcoming_count)}
              color="#6366F1"
            />
            <MetricCard
              icon={CalendarCheck}
              label="Past"
              value={formatInt(metrics.events_summary.past_count)}
              color="#94A3B8"
            />
            <MetricCard
              icon={UserCheck}
              label="Registrations (30d)"
              value={formatInt(metrics.events_summary.registrations_last_30_days)}
              color="#3B82F6"
            />
            <MetricCard
              icon={LogIn}
              label="Check-ins (30d)"
              value={formatInt(metrics.events_summary.check_ins_last_30_days)}
              color="#22C55E"
            />
            <MetricCard
              icon={Percent}
              label="Conversion rate"
              value={`${(metrics.events_summary.check_in_conversion_rate * 100).toFixed(1)}%`}
              color="#F59E0B"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
